import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../store";

// API base URL
const getBaseApiUrl = () => {
  const isBrowser = typeof window !== "undefined";

  const isLocalhost =
    isBrowser &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const localBaseUrl = process.env.REACT_APP_BACKEND_URL;
  const publicBaseUrl =
    process.env.REACT_APP_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_BASE_URL;

  const normalizeBaseUrl = (value: string) => {
    const cleanBaseUrl = value.replace(/\/+$/, "");

    if (cleanBaseUrl.endsWith("/api/v1")) {
      return cleanBaseUrl;
    }

    return `${cleanBaseUrl}/api/v1`;
  };

  /**
   * Local frontend must use local backend.
   * This prevents localhost development from calling production by mistake.
   */
  if (isLocalhost) {
    if (localBaseUrl && localBaseUrl.trim()) {
      return normalizeBaseUrl(localBaseUrl);
    }

    return "http://localhost:5000/api/v1";
  }

  /**
   * Production / WhatsApp browser must never use localhost.
   * This prevents deployed frontend from calling the user's phone/computer localhost.
   */
  if (publicBaseUrl && publicBaseUrl.trim()) {
    return normalizeBaseUrl(publicBaseUrl);
  }

  if (
    localBaseUrl &&
    localBaseUrl.trim() &&
    !localBaseUrl.includes("localhost") &&
    !localBaseUrl.includes("127.0.0.1")
  ) {
    return normalizeBaseUrl(localBaseUrl);
  }

  return "https://api.pickdengu.mw/api/v1";
};

const BASE_API_URL = getBaseApiUrl();
const TRIPS_URL = `${BASE_API_URL}/hike-trips`;
const BOOKINGS_URL = `${BASE_API_URL}/hike/bookings`;

// Types
export interface LocationInfo {
  address: string;
  lat: number;
  lng: number;
}

export type TripStatus = "pending" | "in_progress" | "completed" | "cancelled";
export type TripRecurrenceFrequency = "none" | "daily" | "weekly" | "monthly";

export interface HikeTripRecurrence {
  enabled: boolean;
  frequency: TripRecurrenceFrequency;
  interval: number;
  generateDaysAhead: number;
  startsOn?: string | Date | null;
  endsOn?: string | Date | null;
  lastGeneratedUntil?: string | Date | null;
}

export interface HikeTrip {
  _id: string;
  userId?: string;
  from: LocationInfo;
  to: LocationInfo;
  date: string;
  departDate?: string;
  arriveDate?: string;
  seatsAvailable: number;
  driverId: string;
  carId: string;
  carType: string;
  priceMk: number;
  departTime: string;
  arriveTime: string;
  status?: TripStatus;

  isTemplate?: boolean;
  isGeneratedOccurrence?: boolean;
  recurrenceTemplateId?: string | null;
  occurrenceKey?: string;
  recurrence?: HikeTripRecurrence;
}

interface Booking {
  _id: string;
  tripId: string;
  orderId?: string;
  userId: string;
  ownerUserId?: string;
  seatsBooked: number;
  paidVia: string;
  amountPaid?: number;
  currency?: string;
  paymentStatus?: "pending" | "paid" | "refunded";
  status?: "pending" | "in_progress" | "completed" | "cancelled";
}

interface GenerateRecurringTripsResponse {
  ok: boolean;
  result?: {
    skipped?: boolean;
    reason?: string;
    templatesChecked?: number;
    created?: number;
    skippedExisting?: number;
    skippedInvalid?: number;
  };
  message?: string;
}

// State
interface HikeTripsState {
  list: HikeTrip[];
  loading: boolean;
  error: string | null;
  scheduleGenerationLoading: boolean;
  scheduleGenerationResult: GenerateRecurringTripsResponse | null;
}

const initialState: HikeTripsState = {
  list: [],
  loading: false,
  error: null,
  scheduleGenerationLoading: false,
  scheduleGenerationResult: null,
};

const normalizeTripsResponse = (payload: any): HikeTrip[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.trips)) return payload.trips;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.trips)) return payload.data.trips;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;

  return [];
};

const normalizeTripResponse = (payload: any): HikeTrip => {
  return payload?.trip || payload?.data?.trip || payload?.data || payload;
};

const normalizeBookingResponse = (payload: any): Booking => {
  return payload?.booking || payload?.data?.booking || payload?.data || payload;
};

const getErrorMessage = (err: any, fallback: string) => {
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.response?.data?.error) return err.response.data.error;
  if (typeof err?.response?.data === "string") return err.response.data;
  if (err?.message) return err.message;

  return fallback;
};

// 🔁 Async: Fetch filtered trips
export const fetchTrips = createAsyncThunk<
  HikeTrip[],
  { from: string; to: string; date: string }
>("hikeTrips/fetchTrips", async (filters, thunkAPI) => {
  try {
    const res = await axios.get(TRIPS_URL, { params: filters });
    return normalizeTripsResponse(res.data);
  } catch (err) {
    console.error("Failed to fetch trips:", err);
    return thunkAPI.rejectWithValue(
      getErrorMessage(err, "Failed to fetch trips"),
    );
  }
});

// 🔁 Async: Fetch all trips
export const fetchAllTrips = createAsyncThunk<HikeTrip[], void>(
  "hikeTrips/fetchAllTrips",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(`${TRIPS_URL}/all`);
      return normalizeTripsResponse(res.data);
    } catch (err) {
      console.error("Failed to fetch all trips:", err);
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to fetch all trips"),
      );
    }
  },
);

// 🔁 Async: Generate recurring trip schedules manually
export const generateRecurringTrips = createAsyncThunk<
  GenerateRecurringTripsResponse,
  void
>("hikeTrips/generateRecurringTrips", async (_, thunkAPI) => {
  try {
    const res = await axios.post<GenerateRecurringTripsResponse>(
      `${TRIPS_URL}/schedules/generate`,
    );

    return res.data;
  } catch (err) {
    console.error("Failed to generate recurring trips:", err);
    return thunkAPI.rejectWithValue(
      getErrorMessage(err, "Failed to generate recurring trips"),
    );
  }
});

// 🔁 Async: Add trip
export const addTrip = createAsyncThunk<HikeTrip, Partial<HikeTrip>>(
  "hikeTrips/addTrip",
  async (tripData, thunkAPI) => {
    try {
      const res = await axios.post(TRIPS_URL, tripData);
      return normalizeTripResponse(res.data);
    } catch (err) {
      console.error("Failed to add trip:", err);
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to add trip"),
      );
    }
  },
);

// 🔁 Async: Update trip
export const updateTrip = createAsyncThunk<
  HikeTrip,
  { id: string; updates: Partial<HikeTrip> }
>("hikeTrips/updateTrip", async ({ id, updates }, thunkAPI) => {
  try {
    const res = await axios.put(`${TRIPS_URL}/${id}`, updates);
    return normalizeTripResponse(res.data);
  } catch (err) {
    console.error("Failed to update trip:", err);
    return thunkAPI.rejectWithValue(
      getErrorMessage(err, "Failed to update trip"),
    );
  }
});

// 🔁 Async: Delete trip
export const deleteTrip = createAsyncThunk<string, string>(
  "hikeTrips/deleteTrip",
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${TRIPS_URL}/${id}`);
      return id;
    } catch (err) {
      console.error("Failed to delete trip:", err);
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to delete trip"),
      );
    }
  },
);

// 🔁 Async: Book Seat — returns full booking object for sendTicketEmail
interface BookSeatPayload {
  tripId: string;
  orderId?: string;
  userId: string | undefined;
  seatsBooked: number;
  paidVia: string;
  paymentStatus?: "pending" | "paid" | "refunded";
}

export const bookSeat = createAsyncThunk<Booking, BookSeatPayload>(
  "hikeTrips/bookSeat",
  async (
    {
      tripId,
      orderId = "",
      userId,
      seatsBooked,
      paidVia,
      paymentStatus = "pending",
    },
    thunkAPI,
  ) => {
    try {
      const res = await axios.post<Booking>(BOOKINGS_URL, {
        tripId,
        orderId,
        userId,
        seatsBooked,
        paidVia,
        paymentStatus,
      });

      return normalizeBookingResponse(res.data); // ✅ Now returns full Booking including _id
    } catch (err) {
      console.error("Failed to book seat:", err);
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to book seat"),
      );
    }
  },
);

// 🔍 Trip Details selector
export const getTripById = (state: RootState, tripId: string) =>
  state.hikeTrips.list.find((trip) => trip._id === tripId);

// 🔧 Slice
const hikeTripsSlice = createSlice({
  name: "hikeTrips",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchTrips
      .addCase(fetchTrips.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTrips.fulfilled,
        (state, action: PayloadAction<HikeTrip[]>) => {
          state.loading = false;
          state.list = action.payload;
        },
      )
      .addCase(fetchTrips.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // addTrip
      .addCase(addTrip.fulfilled, (state, action) => {
        state.list.push(action.payload);
      })
      .addCase(addTrip.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // fetchAllTrips
      .addCase(fetchAllTrips.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllTrips.fulfilled,
        (state, action: PayloadAction<HikeTrip[]>) => {
          state.loading = false;
          state.list = action.payload;
        },
      )
      .addCase(fetchAllTrips.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // generateRecurringTrips
      .addCase(generateRecurringTrips.pending, (state) => {
        state.scheduleGenerationLoading = true;
        state.error = null;
      })
      .addCase(generateRecurringTrips.fulfilled, (state, action) => {
        state.scheduleGenerationLoading = false;
        state.scheduleGenerationResult = action.payload;
      })
      .addCase(generateRecurringTrips.rejected, (state, action) => {
        state.scheduleGenerationLoading = false;
        state.error = action.payload as string;
      })

      // updateTrip
      .addCase(updateTrip.fulfilled, (state, action) => {
        const index = state.list.findIndex((t) => t._id === action.payload._id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(updateTrip.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // deleteTrip
      .addCase(deleteTrip.fulfilled, (state, action) => {
        state.list = state.list.filter((t) => t._id !== action.payload);
      })
      .addCase(deleteTrip.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // bookSeat
      .addCase(bookSeat.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export default hikeTripsSlice.reducer;
